"use client";

import { useQuery } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { ExternalLink } from "lucide-react";
import { useAxios } from "@/app/providers/AxiosProvider";
import { useSignalsAccess } from "@/lib/signals/useSignals";
import { Button } from "@/ui/components/Button";
import { Skeleton } from "@/ui/components/Skeleton";
import styles from "./SignalsView.module.css";

export interface EvidenceSource { quote: string; title: string; url: string | null }

function hostname(url: string) {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
}

function Quotes({ sources }: { sources: EvidenceSource[] }) {
  return <ul className={styles.quotes}>{sources.map((source, index) => <li key={index}>
    <blockquote>{source.quote}</blockquote>
    <cite>{source.url && /^https?:\/\//i.test(source.url)
      ? <a className={styles.sourceLink} href={source.url} target="_blank" rel="noopener noreferrer">
        {source.title === "Research source" ? hostname(source.url) : source.title}<ExternalLink size={12} aria-hidden /></a>
      : source.title}</cite>
  </li>)}</ul>;
}

/** Floats records (the CV, notes) on one side, independent public sources on the other. */
export function EvidenceCompare({ sources }: { sources: EvidenceSource[] }) {
  if (!sources.length) return <p className={styles.muted}>No supporting source is currently available.</p>;
  const records = sources.filter((source) => !source.url);
  const external = sources.filter((source) => source.url);
  if (!records.length || !external.length) return <Quotes sources={sources} />;
  return <div className={styles.compare}>
    <section aria-label="From Floats records"><h4>In Floats</h4><Quotes sources={records} /></section>
    <section aria-label="From public sources"><h4>Public sources</h4><Quotes sources={external} /></section>
  </div>;
}

export function SignalEvidence({ id }: { id: string }) {
  const api = useAxios() as AxiosInstance;
  const access = useSignalsAccess();
  const query = useQuery({
    queryKey: ["signals-evidence", access.scope, id],
    queryFn: async ({ signal }) => (await api.get<{ data: EvidenceSource[] }>(`/signals/${id}/evidence`, { signal })).data.data,
    enabled: access.enabled, staleTime: 5 * 60_000, retry: false,
  });
  if (query.isPending) return <div role="status" aria-label="Loading sources" className={styles.compare}>
    <Skeleton variant="rounded" height={72} /><Skeleton variant="rounded" height={72} /></div>;
  if (query.isError) return <p role="alert" className={styles.muted}>Sources could not be loaded. <Button variant="tertiary" size="sm" onClick={() => void query.refetch()}>Try again</Button></p>;
  return <EvidenceCompare sources={query.data} />;
}
